import { useMemo } from 'react'

import { FixedBanner } from '@circlefin/components'
import { ModalLink } from '@circlefin/modal-router'
import { useTravelRuleRequired } from '@features/users/hooks/travel-rule'
import { routes } from '@services/sections/modal'
import {
  BlockchainAddressStatus,
  useBlockchainRecipientAddressWithMetadataQuery,
} from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

export interface PendingAddressVerificationAlertProps {
  /*
   * Address id.
   */
  addressId: string
  /*
   * Transaction id.
   */
  transactionId: string
}

export const PendingAddressVerificationAlert: React.FC<
  PendingAddressVerificationAlertProps
> = ({ addressId, transactionId }) => {
  const { t } = useTranslation('payments')
  const { travelRuleRequired } = useTravelRuleRequired()

  const { data } = useBlockchainRecipientAddressWithMetadataQuery({
    variables: {
      id: addressId,
    },
    skip: !travelRuleRequired,
  })

  const status = useMemo(
    () => data?.blockchainRecipientAddressWithMetadata?.status,
    [data?.blockchainRecipientAddressWithMetadata?.status],
  )

  const visible = useMemo(
    () =>
      travelRuleRequired &&
      status === BlockchainAddressStatus.PENDING_VERIFICATION,
    [status, travelRuleRequired],
  )

  return (
    <FixedBanner level="site" status="warning" visible={visible}>
      <FixedBanner.Title>
        {t('pendingAddressVerificationAlert.title')}
      </FixedBanner.Title>
      <FixedBanner.Description>
        {t('pendingAddressVerificationAlert.description')}
        <ModalLink
          pathname={routes.account.addressBook.verifyAddress.ownerType}
          query={{ transactionId, addressId }}
        >
          {t('pendingAddressVerificationAlert.cta')}
        </ModalLink>
      </FixedBanner.Description>
    </FixedBanner>
  )
}
